/**
 * The apps a company can install, as the shell needs to know them.
 *
 * The server owns the truth — which apps exist, what they depend on, and what
 * this company has installed. What lives here is only what the browser needs
 * and the server has no business knowing: which nav group an app owns, which
 * icon it wears, and where its overview page is.
 *
 * The codes are the generated contract enum, so an app added to the contract
 * and not handled here is a type error rather than a blank screen.
 */
import type { components } from "@erp/api-client";
import {
  Banknote,
  BookOpen,
  Boxes,
  Building2,
  Landmark,
  Layers,
  Plug,
  Receipt,
  Settings,
  ShoppingCart,
  Truck,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";

export type AppCode = components["schemas"]["ModuleCode"];
export type AppStatus = components["schemas"]["ModuleStatus"];
export type AppRecord = components["schemas"]["Module"];

/** Core is the ERP itself and is never absent. */
export const CORE: AppCode = "core";

export interface AppPresentation {
  code: AppCode;
  icon: LucideIcon;
  /**
   * The path prefixes this app's screens sit under.
   *
   * A list rather than a single prefix because the five finance apps share the
   * `/finance` namespace: splitting them was about who owns which screen, not
   * about renaming URLs people already have open. Longest match wins, so
   * `/finance/tax-codes` belongs to Tax even though `/finance/...` belongs to
   * nothing.
   */
  paths: readonly string[];
  /** i18n key prefix for this app's name, tagline and bullets. */
  messageKey: string;
}

/**
 * Where an app's overview lives.
 *
 * Its own namespace rather than the section root, because a section root is
 * not always free: `/partners` is already the party list. One rule that holds
 * for every app beats eight that hold and one exception nobody remembers.
 */
export function appOverviewPath(code: AppCode): string {
  return `/apps/${code}`;
}

/**
 * Every app, in the order the Apps screen shows them: the ones a company can
 * run on their own first, then the ones built on top.
 */
export const APPS: readonly AppPresentation[] = [
  { code: "core", icon: Settings, paths: ["/admin", "/account"], messageKey: "apps.core" },
  { code: "catalog", icon: Layers, paths: ["/catalog"], messageKey: "apps.catalog" },
  {
    code: "partners",
    icon: Users,
    paths: ["/partners", "/settings/partner-groups", "/settings/payment-terms"],
    messageKey: "apps.partners",
  },
  {
    code: "accounting",
    icon: BookOpen,
    // Closing a period and the financial statements moved here from Finance:
    // they are ledger work that only sat under `/finance` by naming accident.
    paths: [
      "/ledger",
      "/finance/period-end",
      "/finance/period-close",
      "/finance/statements",
      "/finance/statement-layouts",
    ],
    messageKey: "apps.accounting",
  },
  {
    code: "tax",
    icon: Receipt,
    // Proofs are the evidence a return is built from, and carry
    // finance.tax-return.read.
    paths: ["/finance/tax-codes", "/finance/proofs"],
    messageKey: "apps.tax",
  },
  { code: "inventory", icon: Boxes, paths: ["/inventory"], messageKey: "apps.inventory" },
  {
    code: "receivables",
    icon: Banknote,
    paths: [
      "/finance/customer-invoices",
      "/finance/customer-receipts",
      "/finance/collections",
      "/finance/gateway",
      "/finance/reports/aging",
    ],
    messageKey: "apps.receivables",
  },
  {
    code: "payables",
    icon: Wallet,
    paths: ["/finance/supplier-invoices", "/finance/payment-runs"],
    messageKey: "apps.payables",
  },
  {
    code: "banking",
    icon: Landmark,
    paths: [
      "/finance/bank-accounts",
      "/finance/bank-statements",
      "/finance/bank-reconciliation",
      "/finance/bank-rules",
    ],
    messageKey: "apps.banking",
  },
  {
    code: "fixed_assets",
    icon: Building2,
    paths: ["/finance/fixed-assets", "/finance/asset-classes", "/finance/depreciation-runs"],
    messageKey: "apps.fixed_assets",
  },
  { code: "purchasing", icon: Truck, paths: ["/purchasing"], messageKey: "apps.purchasing" },
  { code: "sales", icon: ShoppingCart, paths: ["/sales"], messageKey: "apps.sales" },
  { code: "channels", icon: Plug, paths: ["/channels"], messageKey: "apps.channels" },
];

const byCode = new Map<AppCode, AppPresentation>(APPS.map((app) => [app.code, app]));

export function appPresentation(code: AppCode): AppPresentation | undefined {
  return byCode.get(code);
}

/**
 * Which app owns a path.
 *
 * Longest prefix wins so a future nested app cannot be shadowed by its parent.
 * A path no app claims belongs to core — the dashboard, the account page and
 * sign-out are the shell itself, and gating them would be gating the way out.
 */
export function appOwningPath(pathname: string): AppCode {
  let owner: AppCode = CORE;
  let longest = 0;
  for (const app of APPS) {
    for (const prefix of app.paths) {
      if (prefix === "/admin" || prefix === "/account") continue;
      const owns = pathname === prefix || pathname.startsWith(`${prefix}/`);
      if (owns && prefix.length > longest) {
        owner = app.code;
        longest = prefix.length;
      }
    }
  }
  return owner;
}
